import { 
  Box, 
  CircularProgress, 
  IconButton, 
  Button 
} from '@mui/material';
import { ChevronLeft, ChevronRight, Settings } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import ShopifyIntegration from './shopify-integration';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import ShopifyIntegrationConfigForm from './integration-config';
import { ShopifyIntegrationConfig } from '../../../../interfaces/common/shopify';
import ShopifyOrders from './shopify-orders';
import { IconWithText } from '../../../common/styles/components';

const ShopifyDashboardSection = ({style, children}: {style?: React.CSSProperties, children: React.ReactNode}) => {
  return (
    <Box 
      maxWidth={"650px"} 
      width={"100%"}
      border={"1px solid #ddd"}
      borderRadius={6}
      sx={{
        paddingRight: 4, 
        paddingLeft: 4, 
        paddingTop: 2, 
        paddingBottom: 2, 
        ...(style??{}),
        transition: "all 0.3s ease"
      }}
      display={"flex"}
      flexDirection={"column"}
      gap={2}
      alignItems={"center"}
    >
      {children}
    </Box>
  );
};

function ShopifyDashboard() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [connectedShop, setConnectedShop] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shopifyConfig, setShopifyConfig] = useState<ShopifyIntegrationConfig | null>(null);
  const [storeId,setStoreId] = useState<number | null>(null);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    // Initialize from localStorage on first render
    const saved = localStorage.getItem("PT_SHOPIFY_SIDEBAR_OPEN");
    return saved !== null ? JSON.parse(saved) : true; // default: open
  });

  // Persist state changes
  useEffect(() => {
    localStorage.setItem("PT_SHOPIFY_SIDEBAR_OPEN", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  const fetchIntegration = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get<ShopifyIntegrationConfig>(
        `${API_ENDPOINT}/shopify/v1/integration/`,
        config
      );
      setConnectedShop(response.data.shop_url);
      if (response.data.packaging_mode) {
        setShopifyConfig(response.data);
      }
      if (response.data.id) {
        setStoreId(response.data.id);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setConnectedShop(null);
      } else {
        showMessage('Failed to load Shopify integration', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegration();
  }, []);

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        {
          !isSidebarOpen && (
            <Box>
              <IconWithText onClick={() => setIsSidebarOpen(!isSidebarOpen)} >
                <ChevronRight /> Show Integration Details
              </IconWithText>
            </Box>
          )
        }

        <Grid2 container spacing={2} width={"100%"}>
          {/* LEFT SIDEBAR */}
          <Grid2 xs={12} position="relative" md={isSidebarOpen ? 4 : 0} sx={{transition: "all 0.3s ease"}}>
            {isSidebarOpen && shopifyConfig && <Box sx={{position: "absolute", top: 15, right: 25, zIndex: 100, whiteSpace: "nowrap"}}>
              <IconWithText onClick={() => setIsSidebarOpen(!isSidebarOpen)} >
                <ChevronLeft /> Hide
              </IconWithText>
            </Box>}
              
            {isSidebarOpen && (
              <Box 
                width={"100%"}
                display={"flex"} 
                flexDirection={"column"} 
                gap={2} 
                alignItems={"flex-start"}
              >
                <ShopifyDashboardSection>
                  {isLoading ? <CircularProgress /> : <ShopifyIntegration connectedShop={connectedShop} refreshIntegrationData={fetchIntegration} />}

                {storeId && shopifyConfig && (
                    <ShopifyIntegrationConfigForm
                      value={shopifyConfig}
                      storeId={storeId}
                      updateValue={setShopifyConfig}
                    />
                )}
                </ShopifyDashboardSection>
              </Box>
            )}
          </Grid2>

          {/* RIGHT CONTENT */}
          {shopifyConfig && <Grid2 xs={12} md={isSidebarOpen ? 8 : 12} position="relative">
            <ShopifyDashboardSection style={{maxWidth: "100%"}}>
              <ShopifyOrders integrationConfig={shopifyConfig} />
            </ShopifyDashboardSection>
          </Grid2>}
        </Grid2>

        </>
      )}
    </>
  );
}

export default ShopifyDashboard;
